// ============================================================
// SecaoPainelGeral.js — o programa inteiro numa tela
//
// A ficha do atleta responde "como ESTE atleta está". Esta responde a
// pergunta de cima, que ninguém tinha onde fazer: como está o programa.
// Antes dela, a resposta saía de abrir trinta e cinco fichas e anotar num
// papel à parte.
//
// ── Por que é só do administrador, por enquanto ─────────────
//
// O menu desta casa tem um princípio: módulo em construção aparece para
// toda a coordenação, porque esconder de um perfil e mostrar a outro não
// protege nada e faz duas pessoas que trabalham juntas verem menus
// diferentes.
//
// Esta tela não contraria isso. Ela não está escondida de um perfil: está
// em Administração, que já é só do administrador, enquanto ele decide o
// que desta leitura vai para a coordenação. É liberação por etapa, não
// menu diferente para quem senta lado a lado. Quando sair, sai inteira e
// para todos.
//
// ── A cor diz ausência, não juízo ───────────────────────────
//
// A ficha do atleta não colore número por desempenho, de propósito: verde
// e vermelho dizem "bom" e "ruim", e não é a ficha que decide isso. Aqui
// vale o mesmo, com uma exceção deliberada: o âmbar marca MEDIDA QUE
// FALTA. Falta de medida é fato, não avaliação da pessoa, e é a única
// coisa nesta tela sobre a qual se age hoje.
//
// Nenhum número é calculado aqui nem em painelGeral.js. Todos saem das
// mesmas funções da ficha, do Excel e do PDF.
// ============================================================

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  carregarMembrosGestao, carregarAvaliacoesFisicas,
  carregarAvaliacoesTecnicas, carregarTestesFisicos,
} from "../firebase";
import {
  linhasDoPainel, coberturaDoPainel, ultimasDatas, formulasDoNucleo,
} from "../painelGeral";
import { rotuloDoRanking } from "../rankings";
import {
  Card, Estatistica, FileiraDeEstatisticas, Etiqueta,
  Tabela, Linha, Celula, Vazio, Carregando, StatusBox,
  BarraDeFiltros, CampoDeBusca, SeletorDeFiltro,
} from "../componentes";
import SemNucleo from "../componentes/SemNucleo";
import { AZUL, TEXTO, CINZA_ESC, ALERTA, BORDA, ESP, TIPO } from "../tema";

const DIACRITICOS = /[̀-ͯ]/g;
const semAcento = (s) => String(s || "").toLowerCase().normalize("NFD").replace(DIACRITICOS, "");

// Número como se escreve em português, e "não medido" quando não há.
//
// Travessão, e não zero: zero numa coluna de medida é uma medida.
function num(v, casas = 1) {
  if (!Number.isFinite(v)) return <span style={{ color: CINZA_ESC }}>—</span>;
  return String(Number(v).toFixed(casas)).replace(".", ",");
}

function dataBR(iso) {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}

// A evolução do escore desde a primeira avaliação, com o sinal à frente.
function evolucao(e) {
  if (!e || !Number.isFinite(e.absoluta)) return null;
  const sinal = e.absoluta > 0 ? "+" : "";
  return `${sinal}${String(e.absoluta.toFixed(1)).replace(".", ",")}`;
}

const COLUNAS = [
  { rotulo: "Atleta", chave: "nome", alinha: "left" },
  { rotulo: "Mod.", chave: "modalidade" },
  { rotulo: "Idade", chave: "idade" },
  { rotulo: "Escore global", chave: "escore" },
  { rotulo: "Téc.", chave: "tecnica" },
  { rotulo: "Testes", chave: "testes" },
  { rotulo: "IMC", chave: "imc" },
  { rotulo: "% gordura", chave: "gordura" },
  { rotulo: "Somatotipo", chave: "somatotipo", alinha: "left" },
  { rotulo: "Maturação", chave: "maturacao" },
  { rotulo: "Ranking", chave: "ranking" },
  { rotulo: "Medido em", chave: "medidoEm" },
];

// O valor pelo qual cada coluna ordena. Fora daqui a tabela ordenaria
// pelo objeto, que em JavaScript compara como texto e embaralha.
function valorDaChave(l, chave) {
  switch (chave) {
    case "escore": return l.escore ? l.escore.global : null;
    case "tecnica": return l.tecnica ? l.tecnica.media : null;
    case "testes": return l.testes ? l.testes.media : null;
    case "imc": return l.fisica ? l.fisica.imc : null;
    case "gordura": return l.fisica ? l.fisica.gordura : null;
    case "somatotipo": return l.fisica ? l.fisica.somatotipo : null;
    case "maturacao": return l.fisica ? l.fisica.maturacao : null;
    case "ranking": {
      const n = l.ranking && Number(l.ranking.estadual);
      return Number.isFinite(n) ? n : null;
    }
    case "medidoEm": return medidoEm(l);
    default: return l[chave];
  }
}

// A medida mais recente do atleta, qualquer que seja o tipo. É o que
// responde "quando este atleta foi visto pela última vez".
function medidoEm(l) {
  return [l.fisica, l.tecnica, l.testes]
    .map((m) => m && m.data).filter(Boolean).sort().pop() || null;
}

// Ausente vai sempre para o fim, nas duas direções.
//
// Senão, ordenar por IMC crescente encheria a primeira tela de quem não
// tem IMC, que é exatamente quem aquela ordenação não está procurando.
function ordenar(linhas, chave, direcao) {
  const fator = direcao === "desc" ? -1 : 1;
  return [...linhas].sort((a, b) => {
    const x = valorDaChave(a, chave);
    const y = valorDaChave(b, chave);
    const vazioX = x === null || x === undefined || x === "";
    const vazioY = y === null || y === undefined || y === "";
    if (vazioX && vazioY) return String(a.nome).localeCompare(String(b.nome), "pt-BR");
    if (vazioX) return 1;
    if (vazioY) return -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * fator;
    return String(x).localeCompare(String(y), "pt-BR") * fator;
  });
}

export default function SecaoPainelGeral({ userNucleo }) {
  const nucleo = userNucleo || "";
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [modalidade, setModalidade] = useState("Todas");
  const [busca, setBusca] = useState("");
  const [soAtivos, setSoAtivos] = useState(true);
  const [ordem, setOrdem] = useState({ chave: "escore", direcao: "desc" });
  const [faltantes, setFaltantes] = useState(null);

  const carregar = useCallback(async () => {
    if (!nucleo) return;
    setErro(null);
    setDados(null);
    try {
      // As mesmas quatro leituras da ficha do atleta, e de uma vez: a
      // ficha já baixa o núcleo inteiro para mostrar um atleta, então
      // esta tela não custa mais do que abrir aquela.
      const [membros, fisicas, tecnicas, testes] = await Promise.all([
        carregarMembrosGestao(nucleo),
        carregarAvaliacoesFisicas(nucleo),
        carregarAvaliacoesTecnicas(nucleo),
        carregarTestesFisicos(nucleo),
      ]);
      setDados(linhasDoPainel({ atletas: membros, fisicas, tecnicas, testes }));
    } catch (e) {
      setErro("Não foi possível carregar o painel: " + (e.message || ""));
    }
  }, [nucleo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Em useMemo, e não `dados || []` solto: a lista vazia criada no render
  // seria um array novo toda vez, e invalidaria todos os useMemo abaixo,
  // que recalculariam o painel inteiro a cada tecla digitada na busca.
  const linhas = useMemo(() => dados || [], [dados]);

  const modalidades = useMemo(() => (
    [...new Set(linhas.map((l) => l.modalidade).filter(Boolean))].sort()
  ), [linhas]);

  const visiveis = useMemo(() => {
    const alvo = semAcento(busca).trim();
    const filtradas = linhas.filter((l) => {
      if (soAtivos && !l.ativo) return false;
      if (modalidade !== "Todas" && l.modalidade !== modalidade) return false;
      if (alvo && !semAcento(l.nome).includes(alvo)) return false;
      return true;
    });
    return ordenar(filtradas, ordem.chave, ordem.direcao);
  }, [linhas, soAtivos, modalidade, busca, ordem]);

  const cobertura = useMemo(() => coberturaDoPainel(
    modalidade === "Todas" ? linhas : linhas.filter((l) => l.modalidade === modalidade)
  ), [linhas, modalidade]);

  const datas = useMemo(() => ultimasDatas(linhas), [linhas]);

  // O painel só sabe ler modalidade que tem bateria de testes e modelo de
  // critérios. Pergunta pela fórmula, e não pelo nome do núcleo: no dia em
  // que a canoagem tiver as dela, o painel daquele núcleo passa a
  // funcionar sozinho.
  const formulas = useMemo(() => formulasDoNucleo(linhas), [linhas]);

  function trocarOrdem(chave) {
    setOrdem((o) => (
      o.chave === chave
        ? { chave, direcao: o.direcao === "asc" ? "desc" : "asc" }
        : { chave, direcao: "desc" }
    ));
  }

  // Depois dos hooks, de propósito: sair antes deles quebraria a ordem
  // que o React exige.
  if (!nucleo) return <SemNucleo oQueFaria="ver o painel deste núcleo" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ESP.lg }}>
      {/* A abertura descreve a tabela, então só entra quando há tabela:
          num núcleo sem fórmula ela prometeria o que não vem, e quem lê
          procuraria a tabela abaixo. Lá o cartão que explica a ausência
          ocupa este lugar. */}
      {(!dados || formulas.comFormula.length > 0) && (
        <Card>
          <div style={{ ...TIPO.corpo, color: CINZA_ESC, lineHeight: 1.6 }}>
            Uma linha por atleta, com a última de cada medida: avaliação física,
            avaliação técnica, testes e o escore global, mais o ranking da
            federação. Todos os números saem das mesmas funções que alimentam a
            ficha do atleta, para os dois lugares nunca discordarem.
          </div>
        </Card>
      )}

      {erro && <StatusBox tipo="erro" msg={erro} />}
      {!dados && !erro && <Card><Carregando linhas={5} /></Card>}

      {dados && !formulas.comFormula.length && (
        <Card titulo="Ainda não há fórmulas para as modalidades deste núcleo">
          <div style={{ ...TIPO.corpo, color: CINZA_ESC, lineHeight: 1.7 }}>
            A avaliação técnica e os testes físicos não são genéricos: cada
            modalidade tem a sua bateria de testes e os seus critérios, com as
            tabelas de nota por idade. Hoje existem para{" "}
            <b style={{ color: TEXTO }}>BMX, skate e tênis de mesa</b>.
            {formulas.semFormula.length > 0 && (
              <>
                {" "}Este núcleo treina{" "}
                <b style={{ color: TEXTO }}>{formulas.semFormula.join(", ")}</b>,
                que ainda não têm as suas.
              </>
            )}
            <br /><br />
            Enquanto não tiverem, o painel não mostra a tabela: ela sairia só
            com traços, e página de traços não se lê como "falta a fórmula
            desta modalidade", se lê como programa parado.
          </div>
        </Card>
      )}

      {dados && formulas.comFormula.length > 0 && (
        <>
          {/* Núcleo com uma parte das modalidades sem fórmula: a tabela
              vale para as que têm, e as outras precisam ser ditas, senão
              o atleta de canoagem aparece como quem nunca foi medido. */}
          {formulas.semFormula.length > 0 && (
            <StatusBox
              tipo="info"
              msg={`Sem fórmula ainda: ${formulas.semFormula.join(", ")}. `
                + "Os atletas dessas modalidades aparecem sem avaliação técnica e sem testes "
                + "porque as tabelas de nota delas ainda não existem, e não porque não foram medidos."}
            />
          )}

          <Card titulo="Quem já foi medido">
            <FileiraDeEstatisticas minimo={170}>
              {cobertura.map((c) => (
                <Estatistica
                  key={c.chave}
                  rotulo={c.rotulo}
                  valor={`${c.com}/${c.total}`}
                  cor={c.semMedida.length ? ALERTA : AZUL}
                  contexto={c.semMedida.length
                    ? `${c.semMedida.length} sem medida, clique para ver`
                    : "todos os ativos medidos"}
                  acao={c.semMedida.length
                    ? () => setFaltantes(faltantes === c.chave ? null : c.chave)
                    : undefined}
                />
              ))}
            </FileiraDeEstatisticas>

            {faltantes && (
              <div style={{
                marginTop: ESP.md, padding: "12px 14px", border: `1px solid ${BORDA}`,
                borderRadius: 10, background: "#FFFBEB",
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: TEXTO, marginBottom: 6 }}>
                  Sem {(cobertura.find((c) => c.chave === faltantes) || {}).rotulo}
                </div>
                <div style={{ fontSize: 12.5, color: CINZA_ESC, lineHeight: 1.7 }}>
                  {(cobertura.find((c) => c.chave === faltantes) || { semMedida: [] })
                    .semMedida.join(", ")}
                </div>
              </div>
            )}

            {/* Cobertura cheia com a última coleta de meses atrás não é um
                programa em dia, e o número de cima não diria isso. */}
            <div style={{
              marginTop: ESP.md, display: "flex", gap: ESP.md,
              flexWrap: "wrap", alignItems: "center",
            }}>
              {datas.map((d) => (
                <div key={d.chave} style={{ fontSize: 12.5, color: CINZA_ESC }}>
                  Última {d.rotulo.toLowerCase()}: <b style={{ color: TEXTO }}>{dataBR(d.data)}</b>
                </div>
              ))}
            </div>
          </Card>

          <Card titulo={`Atletas (${visiveis.length})`}>
            <BarraDeFiltros>
              <CampoDeBusca
                valor={busca}
                onChange={setBusca}
                placeholder="Buscar atleta pelo nome"
              />
              <SeletorDeFiltro
                valor={modalidade}
                onChange={setModalidade}
                opcoes={["Todas", ...modalidades]}
              />
              <label style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                fontSize: 12.5, color: CINZA_ESC, cursor: "pointer",
              }}>
                <input
                  type="checkbox"
                  checked={soAtivos}
                  onChange={(e) => setSoAtivos(e.target.checked)}
                />
                Só atletas ativos
              </label>
            </BarraDeFiltros>

            {!visiveis.length ? (
              <Vazio titulo="Nenhum atleta com esse filtro">
                Troque a modalidade ou limpe a busca.
              </Vazio>
            ) : (
              <Tabela
                colunas={COLUNAS}
                ordenarPor={ordem.chave}
                direcao={ordem.direcao}
                onOrdenar={trocarOrdem}
              >
                {visiveis.map((l) => (
                  <Linha key={l.uid} zebra>
                    {/* O nome não quebra: com doze colunas a tabela já
                        rola na horizontal, e deixar "Adrian Dastan
                        Ferreira da Silva" virar quatro linhas faz cada
                        linha da tabela ocupar a altura de quatro. */}
                    <Celula alinha="left" style={{ whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 600, color: TEXTO }}>{l.nome}</div>
                      {!l.ativo && (
                        <div style={{ fontSize: 11, color: CINZA_ESC }}>desligado</div>
                      )}
                    </Celula>
                    <Celula>{l.modalidade || "—"}</Celula>
                    <Celula>{Number.isFinite(l.idade) ? l.idade : "—"}</Celula>

                    {/* O escore global primeiro, porque é o número que a
                        coordenação procura, e a evolução ao lado dele:
                        7,3 não diz nada sem o 6,1 de onde ele veio. */}
                    <Celula>
                      {l.escore ? (
                        <>
                          <b style={{ color: AZUL }}>{num(l.escore.global, 1)}</b>
                          {evolucao(l.escore.desdeOInicio) && (
                            <div style={{ fontSize: 11, color: CINZA_ESC }}>
                              {evolucao(l.escore.desdeOInicio)} desde {dataBR(l.escore.dataDoInicio)}
                            </div>
                          )}
                        </>
                      ) : <Etiqueta tom="atencao">sem escore</Etiqueta>}
                    </Celula>

                    <Celula>
                      {l.tecnica ? (
                        <>
                          {num(l.tecnica.media, 2)}
                          <div style={{ fontSize: 11, color: CINZA_ESC }}>
                            {num(l.tecnica.somatorio, 1)} em {l.tecnica.divisor}
                          </div>
                        </>
                      ) : <Etiqueta tom="atencao">não avaliado</Etiqueta>}
                    </Celula>

                    <Celula>
                      {l.testes ? num(l.testes.media, 2)
                        : <Etiqueta tom="atencao">sem coleta</Etiqueta>}
                    </Celula>

                    <Celula>{l.fisica ? num(l.fisica.imc, 2) : "—"}</Celula>

                    {/* Gordura em branco não é falha de medição: no adulto
                        o protocolo pede dobras que o programa não coleta
                        hoje, e o somatotipo é o que responde por ele. */}
                    <Celula>
                      {l.fisica && Number.isFinite(l.fisica.gordura) ? (
                        <>
                          {num(l.fisica.gordura, 1)}
                          <div style={{ fontSize: 11, color: CINZA_ESC }}>
                            {l.fisica.protocoloDaGordura}
                          </div>
                        </>
                      ) : "—"}
                    </Celula>

                    <Celula alinha="left">
                      {(l.fisica && l.fisica.somatotipo) || "—"}
                    </Celula>
                    <Celula>{(l.fisica && l.fisica.maturacao) || "—"}</Celula>

                    <Celula>
                      {l.ranking ? (
                        <>
                          <div>{rotuloDoRanking(l.ranking.estadual) || "—"} <span style={{ color: CINZA_ESC, fontSize: 11 }}>CE</span></div>
                          <div style={{ fontSize: 11, color: CINZA_ESC }}>
                            {rotuloDoRanking(l.ranking.nacional) || "—"} BR · {l.ranking.ano}
                          </div>
                        </>
                      ) : "—"}
                    </Celula>

                    <Celula>{dataBR(medidoEm(l))}</Celula>
                  </Linha>
                ))}
              </Tabela>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
